interface ScorePoint {
  sessionId: string;
  score: number;
  date: number;
  difficulty: string;
}

interface Props {
  scores: ScorePoint[];
  height?: number;
}

const DIFFICULTY_COLORS = {
  easy: '#22c55e',    // green
  medium: '#f97316',  // orange
  hard: '#ef4444',    // red
};

export function ScoreTrendChart({ scores, height = 120 }: Props) {
  if (scores.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-slate-800 rounded-lg border border-slate-700"
        style={{ height }}
      >
        <p className="text-slate-500 text-sm">No data yet</p>
      </div>
    );
  }

  // Take last 10 scores for display
  const displayScores = scores.slice(-10);
  const maxScore = 100;
  const minScore = 0;
  const range = maxScore - minScore;

  // Chart dimensions
  const padding = { top: 20, right: 20, bottom: 30, left: 40 };
  const chartWidth = 400;
  const chartHeight = height;
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;

  // Calculate points
  const points = displayScores.map((s, i) => ({
    x: padding.left + (i / Math.max(displayScores.length - 1, 1)) * plotWidth,
    y: padding.top + (1 - (s.score - minScore) / range) * plotHeight,
    ...s,
  }));

  // Create path
  const pathD = points
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(' ');

  // Create area path (for gradient fill)
  const areaD = `${pathD} L ${points[points.length - 1].x} ${padding.top + plotHeight} L ${points[0].x} ${padding.top + plotHeight} Z`;

  // Y-axis labels
  const yLabels = [0, 50, 100];

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-slate-400">Score Trend</h3>
        <div className="flex gap-2 text-xs">
          {Object.entries(DIFFICULTY_COLORS).map(([diff, color]) => (
            <div key={diff} className="flex items-center gap-1">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-slate-500 capitalize">{diff}</span>
            </div>
          ))}
        </div>
      </div>

      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="w-full"
        style={{ height }}
      >
        {/* Gradient definition */}
        <defs>
          <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Y-axis grid lines and labels */}
        {yLabels.map((label) => {
          const y = padding.top + (1 - label / 100) * plotHeight;
          return (
            <g key={label}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="#374151"
                strokeDasharray="4"
              />
              <text
                x={padding.left - 8}
                y={y}
                fill="#64748b"
                fontSize="10"
                textAnchor="end"
                dominantBaseline="middle"
              >
                {label}%
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        {points.length > 1 && (
          <path d={areaD} fill="url(#scoreGradient)" />
        )}

        {/* Line */}
        {points.length > 1 && (
          <path
            d={pathD}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Points */}
        {points.map((point, i) => (
          <g key={i}>
            {/* Outer ring (difficulty color) */}
            <circle
              cx={point.x}
              cy={point.y}
              r="6"
              fill={DIFFICULTY_COLORS[point.difficulty as keyof typeof DIFFICULTY_COLORS] || '#3b82f6'}
              opacity="0.3"
            />
            {/* Inner dot */}
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill={DIFFICULTY_COLORS[point.difficulty as keyof typeof DIFFICULTY_COLORS] || '#3b82f6'}
            />
            {/* Score label on hover */}
            <title>
              {new Date(point.date).toLocaleDateString()}: {Math.round(point.score)}% ({point.difficulty})
            </title>
          </g>
        ))}

        {/* X-axis labels (dates) */}
        {displayScores.length <= 5 ? (
          // Show all dates if 5 or fewer points
          points.map((point, i) => (
            <text
              key={i}
              x={point.x}
              y={chartHeight - 8}
              fill="#64748b"
              fontSize="9"
              textAnchor="middle"
            >
              {new Date(point.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </text>
          ))
        ) : (
          // Show first and last only for more points
          <>
            <text
              x={points[0].x}
              y={chartHeight - 8}
              fill="#64748b"
              fontSize="9"
              textAnchor="start"
            >
              {new Date(displayScores[0].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </text>
            <text
              x={points[points.length - 1].x}
              y={chartHeight - 8}
              fill="#64748b"
              fontSize="9"
              textAnchor="end"
            >
              {new Date(displayScores[displayScores.length - 1].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
